import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './SessionManager.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * SessionManager Component
 * 
 * Displays all active sessions for the current user with:
 * - Device icons (💻 Desktop, 📱 Mobile, 📲 Tablet)
 * - Device name, browser, OS
 * - Last activity timestamp
 * - Masked IP address
 * - "This Device" badge for current session
 * - "End Session" button (except for current)
 * 
 * Auto-refreshes every 30 seconds
 */
const SessionManager = () => {
  const [sessions, setSessions] = useState([]);
  const [sessionCount, setSessionCount] = useState({ active_count: 0, max_allowed: 3, at_limit: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endingSession, setEndingSession] = useState(null);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('veripass_token');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}/api/v1/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessions(response.data.sessions);
      setSessionCount({
        active_count: response.data.count,
        max_allowed: response.data.max_allowed,
        at_limit: response.data.at_limit
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSessions, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleEndSession = async (sessionId) => {
    if (!window.confirm('End this session? The device will be logged out.')) {
      return;
    }

    setEndingSession(sessionId);

    try {
      const token = localStorage.getItem('veripass_token');
      await axios.post(
        `${API_BASE}/api/v1/sessions/${sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh sessions list
      await fetchSessions();
    } catch (err) {
      console.error('Failed to end session:', err);
      alert(err.response?.data?.detail || 'Failed to end session');
    } finally {
      setEndingSession(null);
    }
  };

  const handleEndAllSessions = async () => {
    if (!window.confirm('End all other sessions? All other devices will be logged out.')) {
      return;
    }

    try {
      const token = localStorage.getItem('veripass_token');
      await axios.post(
        `${API_BASE}/api/v1/sessions/end-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh sessions list
      await fetchSessions();
    } catch (err) {
      console.error('Failed to end all sessions:', err);
      alert(err.response?.data?.detail || 'Failed to end sessions');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return '📱';
      case 'tablet': return '📲';
      case 'desktop':
      default: return '💻';
    }
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const now = new Date();
    const lastActivity = new Date(timestamp);
    const diffMs = now - lastActivity;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="session-manager">
        <div className="loading">Loading sessions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="session-manager">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="session-manager">
      <div className="session-header">
        <h3>Active Sessions</h3>
        <div className="session-count">
          <span className={sessionCount.at_limit ? 'at-limit' : ''}>
            {sessionCount.active_count} / {sessionCount.max_allowed} sessions active
          </span>
          {sessionCount.at_limit && (
            <span className="warning-badge">⚠️ At limit</span>
          )}
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="no-sessions">No active sessions</div>
      ) : (
        <>
          <div className="sessions-list">
            {sessions.map((session) => (
              <div 
                key={session.session_id} 
                className={`session-card ${session.is_current ? 'current-session' : ''}`}
              >
                <div className="session-icon">
                  {getDeviceIcon(session.device_type)}
                </div>
                
                <div className="session-details">
                  <div className="session-device">
                    <strong>{session.device_name || 'Unknown Device'}</strong>
                    {session.is_current && (
                      <span className="current-badge">This Device</span>
                    )}
                  </div>
                  
                  <div className="session-info">
                    {session.browser && <span>{session.browser}</span>}
                    {session.os && <span> • {session.os}</span>}
                  </div>
                  
                  <div className="session-meta">
                    <span className="activity">
                      Last active: {formatLastActivity(session.last_activity_at)}
                    </span>
                    {session.ip_address && (
                      <span className="ip"> • IP: {session.ip_address}</span>
                    )}
                    {session.city && session.country && (
                      <span className="location"> • {session.city}, {session.country}</span>
                    )}
                  </div>
                </div>

                {!session.is_current && (
                  <button
                    className="btn-end-session"
                    onClick={() => handleEndSession(session.session_id)}
                    disabled={endingSession === session.session_id}
                  >
                    {endingSession === session.session_id ? 'Ending...' : 'End Session'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {sessions.length > 1 && (
            <div className="session-actions">
              <button
                className="btn-end-all"
                onClick={handleEndAllSessions}
              >
                End All Other Sessions
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SessionManager;
